// src/components/AuthPage.js
import { useState } from "react";
import { useAuth } from "../AuthContext";
import { saveUserPreferences } from "../userService";
import { STORES } from "../constants";

export default function AuthPage() {
  const { loginWithGoogle, loginWithEmail, registerWithEmail } = useAuth();
  const [mode, setMode] = useState("login");
  const [step, setStep] = useState(1); // 1 = credentials, 2 = store preferences
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedStores, setSelectedStores] = useState(STORES.map(s => s.name)); // all selected by default
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState(null); // hold user between steps

  function toggleStore(storeName) {
    setSelectedStores(prev =>
      prev.includes(storeName)
        ? prev.filter(s => s !== storeName)
        : [...prev, storeName]
    );
  }

  async function handleCredentials(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await loginWithEmail(email, password);
      } else {
        if (!name.trim()) { setError("Please enter your name."); setLoading(false); return; }
        const cred = await registerWithEmail(email, password, name.trim());
        setNewUser(cred.user);
        setStep(2); // go to store preferences
      }
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  async function handleStorePreferences() {
    setLoading(true);
    try {
      // Save with at least one store, fallback to all if none selected
      const stores = selectedStores.length > 0 ? selectedStores : STORES.map(s => s.name);
      await saveUserPreferences(newUser.uid, { stores });
      // Auth state change will automatically log them in
    } catch (err) {
      console.error("Failed to save preferences:", err);
      // Still proceed even if preferences fail to save
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  function friendlyError(code) {
    switch (code) {
      case "auth/user-not-found": return "No account found with that email.";
      case "auth/wrong-password": return "Incorrect password.";
      case "auth/email-already-in-use": return "An account with that email already exists.";
      case "auth/weak-password": return "Password must be at least 6 characters.";
      case "auth/invalid-email": return "Please enter a valid email address.";
      case "auth/popup-closed-by-user": return "Google sign-in was cancelled.";
      default: return "Something went wrong. Please try again.";
    }
  }

  // ── Step 2: Store Preferences ─────────────────────────────────────────────
  if (step === 2) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Homie" style={{ width: 64, height: 64, objectFit: "contain", marginBottom: 10 }} />
            <h2 style={styles.stepTitle}>Where do you shop?</h2>
            <p style={styles.stepSubtitle}>
              Select your preferred stores and we'll only show those links on your parts lists.
              You can change this anytime in Settings.
            </p>
          </div>

          <div style={styles.storeGrid}>
            {STORES.map(store => {
              const selected = selectedStores.includes(store.name);
              return (
                <button
                  key={store.name}
                  type="button"
                  onClick={() => toggleStore(store.name)}
                  style={{
                    ...styles.storeBtn,
                    borderColor: selected ? store.color : "#e8d5b8",
                    background: selected ? store.color + "18" : "white",
                    transform: selected ? "scale(1.02)" : "scale(1)",
                  }}
                >
                  <div style={styles.storeCheck}>
                    {selected && <span style={{ color: store.color, fontSize: 18, fontWeight: 900 }}>✓</span>}
                  </div>
                  <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 15, fontWeight: 800, color: selected ? store.color : "#7a5c3a" }}>
                    {store.name}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedStores.length === 0 && (
            <p style={{ textAlign: "center", fontSize: 12, color: "#a07850", fontWeight: 700, marginBottom: 8 }}>
              No stores selected — all stores will show by default.
            </p>
          )}

          <button
            style={styles.submitBtn}
            onClick={handleStorePreferences}
            disabled={loading}
          >
            {loading ? "Saving..." : "Continue to Homie →"}
          </button>

          <button
            style={styles.skipBtn}
            onClick={handleStorePreferences}
          >
            Skip — show all stores
          </button>
        </div>
      </div>
    );
  }

  // ── Step 1: Login / Register ──────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Homie" style={styles.logoImg} />
          <div style={styles.logoText}>Homie Fix It</div>
          <div style={styles.logoSub}>Your Home. Your Repairs. Simplified.</div>
        </div>

        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(mode === "login" ? styles.tabActive : {}) }} onClick={() => { setMode("login"); setError(""); }}>Sign In</button>
          <button style={{ ...styles.tab, ...(mode === "register" ? styles.tabActive : {}) }} onClick={() => { setMode("register"); setError(""); }}>Create Account</button>
        </div>

        <button style={styles.googleBtn} onClick={handleGoogle} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: 10, flexShrink: 0 }}>
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
          </svg>
          Continue with Google
        </button>

        <div style={styles.divider}><span style={styles.dividerText}>or</span></div>

        <form onSubmit={handleCredentials}>
          {mode === "register" && (
            <div style={styles.field}>
              <label style={styles.label}>Your Name</label>
              <input style={styles.input} type="text" placeholder="Tim Taylor" value={name} onChange={e => setName(e.target.value)} required />
            </div>
          )}
          <div style={styles.field}>
            <label style={styles.label}>Email Address</label>
            <input style={styles.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input style={styles.input} type="password" placeholder={mode === "register" ? "At least 6 characters" : "••••••••"} value={password} onChange={e => setPassword(e.target.value)} required />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.submitBtn} type="submit" disabled={loading}>
            {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Next →"}
          </button>
        </form>

        <p style={styles.switchText}>
          {mode === "login" ? "Don't have an account? " : "Already have an account? "}
          <button style={styles.switchLink} onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
            {mode === "login" ? "Create one" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #8B4513 0%, #CD853F 50%, #f97316 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20, fontFamily: "'Nunito', sans-serif",
  },
  card: {
    background: "white", borderRadius: 24, padding: "40px 36px",
    width: "100%", maxWidth: 440,
    boxShadow: "0 20px 60px rgba(139,69,19,0.35)",
  },
  logoWrap: { textAlign: "center", marginBottom: 28 },
  logoImg: { width: 72, height: 72, objectFit: "contain", marginBottom: 8 },
  logoText: { fontFamily: "'Fredoka One', cursive", fontSize: 32, color: "#8B4513", lineHeight: 1 },
  logoSub: { fontSize: 13, color: "#a07850", fontWeight: 700, marginTop: 4 },
  tabs: {
    display: "flex", background: "#f5ead8", borderRadius: 12,
    padding: 4, marginBottom: 24,
  },
  tab: {
    flex: 1, padding: "10px 0", border: "none", background: "none",
    borderRadius: 10, cursor: "pointer", fontFamily: "'Nunito', sans-serif",
    fontSize: 14, fontWeight: 700, color: "#a07850", transition: "all 0.18s",
  },
  tabActive: { background: "white", color: "#8B4513", boxShadow: "0 2px 8px rgba(139,69,19,0.15)" },
  googleBtn: {
    width: "100%", padding: "12px 16px", border: "2px solid #e8d5b8",
    borderRadius: 12, background: "white", cursor: "pointer",
    fontFamily: "'Nunito', sans-serif", fontSize: 15, fontWeight: 700,
    color: "#2c1810", display: "flex", alignItems: "center",
    justifyContent: "center", transition: "all 0.18s", marginBottom: 20,
  },
  divider: { position: "relative", textAlign: "center", marginBottom: 20, borderTop: "2px solid #f0e0cc" },
  dividerText: { position: "relative", top: -11, background: "white", padding: "0 12px", color: "#c09870", fontSize: 13, fontWeight: 700 },
  field: { marginBottom: 16 },
  label: { display: "block", fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a07850", marginBottom: 6 },
  input: { width: "100%", padding: "11px 14px", border: "2px solid #e8d5b8", borderRadius: 10, fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 600, color: "#2c1810", background: "#fffaf4", outline: "none", boxSizing: "border-box" },
  error: { background: "#fee2e2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 700, marginBottom: 14 },
  submitBtn: {
    width: "100%", padding: "13px", border: "none",
    background: "linear-gradient(135deg, #ff8c00, #f97316)",
    borderRadius: 12, color: "white", fontFamily: "'Nunito', sans-serif",
    fontSize: 16, fontWeight: 800, cursor: "pointer",
    boxShadow: "0 4px 14px rgba(249,115,22,0.4)", marginTop: 4, transition: "all 0.18s",
  },
  skipBtn: {
    width: "100%", padding: "10px", border: "none", background: "none",
    color: "#a07850", fontFamily: "'Nunito', sans-serif",
    fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: 8,
    textDecoration: "underline",
  },
  switchText: { textAlign: "center", marginTop: 20, fontSize: 13, fontWeight: 700, color: "#a07850" },
  switchLink: { background: "none", border: "none", color: "#f97316", fontWeight: 800, cursor: "pointer", fontSize: 13, fontFamily: "'Nunito', sans-serif" },
  stepTitle: { fontFamily: "'Fredoka One', cursive", fontSize: 26, color: "#8B4513", marginBottom: 8 },
  stepSubtitle: { fontSize: 13, color: "#a07850", fontWeight: 600, lineHeight: 1.6 },
  storeGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 },
  storeBtn: {
    padding: "16px 12px", borderRadius: 14, border: "2px solid",
    cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
    transition: "all 0.18s", background: "white",
  },
  storeCheck: { width: 24, height: 24, borderRadius: 8, border: "2px solid #e8d5b8", display: "flex", alignItems: "center", justifyContent: "center", background: "white", flexShrink: 0 },
};
